'use client'

import { useState } from 'react'
import { FileText, Shield, Download, Trash2, ExternalLink, ChevronDown, ChevronUp } from 'lucide-react'
import { apiGet } from '@/lib/apiFetch'
import { notify } from '@/lib/notifications'

type ExpandedSection = 'terms' | 'privacy' | null

export function TermsTab() {
  const [exporting, setExporting] = useState(false)
  const [expanded, setExpanded] = useState<ExpandedSection>(null)

  const toggleSection = (section: 'terms' | 'privacy') => {
    setExpanded((current) => (current === section ? null : section))
  }

  const openLegal = (hash: string) => {
    window.open(`/legal${hash}`, '_blank', 'noopener,noreferrer')
  }

  const handleExportData = async () => {
    setExporting(true)
    try {
      const response = await apiGet('/api/v1/user/data-export')
      const data = await response.json() as { success?: boolean; data?: unknown; error?: string }

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to export your data')
      }

      const blob = new Blob([JSON.stringify(data.data, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `totalaccess-my-data-${new Date().toISOString()}.json`
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)

      notify.success('Your data export has started.')
    } catch (error) {
      notify.error(error instanceof Error ? error.message : 'Failed to export your data')
    } finally {
      setExporting(false)
    }
  }

  return (
    <div>
      <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Terms & Privacy</h3>
      <div className="space-y-4 sm:space-y-6">
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 sm:p-6">
          <div className="flex items-center gap-2 mb-3">
            <FileText className="w-5 h-5 text-[var(--theme-primary)]" />
            <h4 className="font-medium text-gray-900 dark:text-gray-100">Terms of Service</h4>
          </div>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Our terms of service outline the rules and regulations for the use of TotalAccess platform.
          </p>
          <button
            onClick={() => toggleSection('terms')}
            className="px-4 py-2 bg-[var(--theme-primary)] text-white rounded-md hover:bg-[var(--theme-primary-dark)] focus:outline-none focus:ring-2 focus:ring-[var(--theme-primary)] flex items-center gap-2"
          >
            {expanded === 'terms' ? 'Hide Terms of Service' : 'View Terms of Service'}
            {expanded === 'terms' ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </button>

          {expanded === 'terms' && (
            <div className="mt-4 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-white dark:bg-gray-900">
              <iframe
                title="Terms of Service"
                src="/legal?section=terms#terms"
                className="w-full h-[60vh]"
                loading="lazy"
              />
            </div>
          )}
        </div>

        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 sm:p-6">
          <div className="flex items-center gap-2 mb-3">
            <Shield className="w-5 h-5 text-[var(--theme-primary)]" />
            <h4 className="font-medium text-gray-900 dark:text-gray-100">Privacy Policy</h4>
          </div>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Learn how we collect, use, and protect your personal information.
          </p>
          <button
            onClick={() => toggleSection('privacy')}
            className="px-4 py-2 bg-[var(--theme-primary)] text-white rounded-md hover:bg-[var(--theme-primary-dark)] focus:outline-none focus:ring-2 focus:ring-[var(--theme-primary)] flex items-center gap-2"
          >
            {expanded === 'privacy' ? 'Hide Privacy Policy' : 'View Privacy Policy'}
            {expanded === 'privacy' ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </button>

          {expanded === 'privacy' && (
            <div className="mt-4 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-white dark:bg-gray-900">
              <iframe
                title="Privacy Policy"
                src="/legal?section=privacy-policy#privacy-policy"
                className="w-full h-[60vh]"
                loading="lazy"
              />
            </div>
          )}
        </div>

        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 sm:p-6">
          <div className="flex items-center gap-2 mb-3">
            <Download className="w-5 h-5 text-gray-600" />
            <h4 className="font-medium text-gray-900 dark:text-gray-100">Data Export</h4>
          </div>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Download a copy of all your personal data stored on our platform.
          </p>
          <button
            onClick={handleExportData}
            disabled={exporting}
            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {exporting ? (
              <>
                <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                Exporting...
              </>
            ) : (
              <>
                <Download className="w-4 h-4" />
                Export My Data
              </>
            )}
          </button>
        </div>

        <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4 sm:p-6 border border-red-200 dark:border-red-800">
          <div className="flex items-center gap-2 mb-3">
            <Trash2 className="w-5 h-5 text-red-600" />
            <h4 className="font-medium text-red-900 dark:text-red-300">Danger Zone</h4>
          </div>
          <p className="text-red-700 dark:text-red-300 mb-4">
            Permanently delete your account and all associated data. This action cannot be undone.
          </p>
          <button
            onClick={() => openLegal('#data-deletion')}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 flex items-center gap-2"
          >
            Delete Account
            <ExternalLink className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  )
}
